// eslint-disable-next-line no-restricted-imports
import { OptionTypeBase, Select, SelectProps } from 'arc';
import { forwardRef } from 'react';
import { GroupBase, SelectInstance } from 'react-select';
import { useInputSizeForApplicationType } from '../../common/hooks/use-input-size-for-application-type';

/**
 * Every BaseSelect explicitly caps the rendered options at this value via arc's Select `maxOptionsToShow`
 * prop, so the limit is visible in our code instead of relying on arc's implicit default. Exported as the
 * single source of truth so consumers that surface a "showing the first N results" hint use the same
 * number as the actual render cap instead of duplicating the magic value.
 */
export const BASE_SELECT_MAX_OPTIONS_TO_SHOW = 300;

type BaseSelectProps<
    OptionType extends OptionTypeBase,
    Multi extends boolean = false
> = SelectProps<OptionType, Multi> & { disableCloseMenuOnScroll?: boolean };

export const BaseSelect = forwardRef(function <
    OptionType extends OptionTypeBase,
    Multi extends boolean = false
>(
    props: BaseSelectProps<OptionType, Multi>,
    ref?: React.Ref<SelectInstance<unknown, false, GroupBase<unknown>> | undefined>
) {
    const { size: propsSize, disableCloseMenuOnScroll, ...otherProps } = props;
    const isTestEnv = window.Cypress;
    const size = useInputSizeForApplicationType(propsSize);

    function onInputChange(e: Event, newValue: string) {
        props.onInputChange?.(e, newValue);
        document.getElementsByClassName('arc-select__menu-list')[0]?.scrollTo?.({ top: 0 });
    }

    return (
        // eslint-disable-next-line react/forbid-elements
        <Select
            matchSort
            maxOptionsToShow={BASE_SELECT_MAX_OPTIONS_TO_SHOW}
            {...otherProps}
            ref={ref}
            closeMenuOnScroll={
                disableCloseMenuOnScroll
                    ? false
                    : (e) => {
                          const target = e.target;
                          return !(
                              target === document ||
                              (target instanceof HTMLElement &&
                                  target?.className?.includes('arc-select__menu-list'))
                          );
                      }
            }
            menuPortalTarget={
                isTestEnv ? undefined : document.getElementById('chakra-toast-portal')
            }
            // @ts-expect-error This wants a number but the var is better.
            menuPortalZIndex="var(--arc-zIndices-toast)"
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.stopPropagation();
                }
            }}
            onInputChange={onInputChange}
            tabSelectsValue
            size={size}
        />
    );
});
